import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Copy, MapPin, Globe, Shield, Wifi } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface IpInfo {
  ip: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  latitude: number;
  longitude: number;
  timezone: string;
  isp: string;
  organization: string;
  asn: string;
  type: string;
}

export const IpLookupTool: React.FC = () => {
  const { t } = useTranslation();
  const [ipAddress, setIpAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [ipInfo, setIpInfo] = useState<IpInfo | null>(null);
  const [error, setError] = useState('');
  const [userIp, setUserIp] = useState('');

  // Get user's IP address on component mount
  useEffect(() => {
    // Mock user IP for demonstration
    setUserIp('203.0.113.1');
  }, []);

  // Mock IP lookup for demonstration
  const mockIpLookup = async (ip: string): Promise<IpInfo> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock data based on IP
    const mockData: Record<string, IpInfo> = {
      '8.8.8.8': {
        ip: '8.8.8.8',
        country: 'United States',
        countryCode: 'US',
        region: 'California',
        city: 'Mountain View',
        latitude: 37.4056,
        longitude: -122.0775,
        timezone: 'America/Los_Angeles',
        isp: 'Google LLC',
        organization: 'Google Public DNS',
        asn: 'AS15169',
        type: 'hosting'
      },
      '1.1.1.1': {
        ip: '1.1.1.1',
        country: 'Australia',
        countryCode: 'AU',
        region: 'New South Wales',
        city: 'Sydney',
        latitude: -33.8688,
        longitude: 151.2093,
        timezone: 'Australia/Sydney',
        isp: 'Cloudflare, Inc.',
        organization: 'APNIC and Cloudflare DNS Resolver project',
        asn: 'AS13335',
        type: 'hosting'
      }
    };

    return mockData[ip] || {
      ip,
      country: 'Unknown',
      countryCode: 'XX',
      region: 'Unknown',
      city: 'Unknown',
      latitude: 0,
      longitude: 0,
      timezone: 'UTC',
      isp: 'Unknown ISP',
      organization: 'Unknown Organization',
      asn: 'Unknown',
      type: 'unknown'
    };
  };

  const handleLookup = async (ip?: string) => {
    const targetIp = ip || ipAddress.trim();
    
    if (!targetIp) {
      setError(t('tools.ipLookup.enterIpError'));
      return;
    }

    // Basic IP validation
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;

    if (!ipv4Regex.test(targetIp) && !ipv6Regex.test(targetIp)) {
      setError(t('tools.ipLookup.invalidIpError'));
      return;
    }

    setLoading(true);
    setError('');
    setIpInfo(null);

    try {
      const data = await mockIpLookup(targetIp);
      setIpInfo(data);
      if (!ip) setIpAddress(targetIp);
    } catch (err) {
      setError(t('tools.ipLookup.lookupError'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const formatIpInfo = (data: IpInfo) => {
    return `${t('tools.ipLookup.ipAddress')}: ${data.ip}
${t('tools.ipLookup.country')}: ${data.country} (${data.countryCode})
${t('tools.ipLookup.region')}: ${data.region}
${t('tools.ipLookup.city')}: ${data.city}
${t('tools.ipLookup.coordinates')}: ${data.latitude}, ${data.longitude}
${t('tools.ipLookup.timezone')}: ${data.timezone}
${t('tools.ipLookup.isp')}: ${data.isp}
${t('tools.ipLookup.organization')}: ${data.organization}
${t('tools.ipLookup.asn')}: ${data.asn}
${t('tools.ipLookup.type')}: ${data.type}`;
  };

  const sampleIps = [
    '8.8.8.8',
    '1.1.1.1',
    '208.67.222.222',
    '9.9.9.9',
    '76.76.19.19'
  ];

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="w-5 h-5" />
            <span>{t('tools.ipLookup.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
                placeholder={t('tools.ipLookup.enterIp')}
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
              />
              <Button
                onClick={() => handleLookup()}
                disabled={loading}
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? t('tools.ipLookup.lookingUp') : t('tools.ipLookup.lookup')}
              </Button>
            </div>

            {userIp && (
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Wifi className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-800 dark:text-blue-200">
                    {t('tools.ipLookup.yourIp')}: {userIp}
                  </span>
                </div>
                <Button
                  onClick={() => handleLookup(userIp)}
                  size="sm"
                  variant="outline"
                >
                  {t('tools.ipLookup.lookupMyIp')}
                </Button>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
                <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.ipLookup.sampleIpAddresses')}:
              </label>
              <div className="flex flex-wrap gap-2">
                {sampleIps.map((sampleIp) => (
                  <Button
                    key={sampleIp}
                    onClick={() => setIpAddress(sampleIp)}
                    variant="outline"
                    size="sm"
                  >
                    {sampleIp}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {ipInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.ipLookup.ipInformationFor')} {ipInfo.ip}</span>
              <Button
                onClick={() => copyToClipboard(formatIpInfo(ipInfo))}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3 mr-1" />
                {t('tools.ipLookup.copyAll')}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Location Info */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  {t('tools.ipLookup.locationInformation')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.country')}:</span>
                      <span className="font-medium">{ipInfo.country} ({ipInfo.countryCode})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.region')}:</span>
                      <span className="font-medium">{ipInfo.region}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.city')}:</span>
                      <span className="font-medium">{ipInfo.city}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.latitude')}:</span>
                      <span className="font-mono">{ipInfo.latitude}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.longitude')}:</span>
                      <span className="font-mono">{ipInfo.longitude}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.timezone')}:</span>
                      <span className="font-mono">{ipInfo.timezone}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Network Info */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  {t('tools.ipLookup.networkInformation')}
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.isp')}:</span>
                    <span className="font-medium">{ipInfo.isp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.organization')}:</span>
                    <span className="font-medium">{ipInfo.organization}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.asn')}:</span>
                    <span className="font-mono">{ipInfo.asn}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('tools.ipLookup.type')}:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                      {ipInfo.type}
                    </span>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('tools.ipLookup.locationMap')}</h4>
                <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p>{t('tools.ipLookup.mapViewPlaceholder')}</p>
                    <p className="text-sm">{t('tools.ipLookup.coordinates')}: {ipInfo.latitude}, {ipInfo.longitude}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.ipLookup.aboutIpLookup')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.ipLookup.whatIsIpGeolocation')}</h4>
              <p>
                {t('tools.ipLookup.ipGeolocationDescription')}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.ipLookup.informationAvailable')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('tools.ipLookup.info1')}</li>
                <li>{t('tools.ipLookup.info2')}</li>
                <li>{t('tools.ipLookup.info3')}</li>
                <li>{t('tools.ipLookup.info4')}</li>
                <li>{t('tools.ipLookup.info5')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.ipLookup.accuracyNote')}</h4>
              <p>
                {t('tools.ipLookup.accuracyDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
